import { Capacitor, registerPlugin } from '@capacitor/core';
import { App } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { Device } from '@capacitor/device';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Network } from '@capacitor/network';
import { stack as router } from '../navigation/StackNavigator';

export const isNative = Capacitor.isNativePlatform();

// ==============================
// Custom WiFi plugin (WifiPlugin.java)
// ==============================
interface WifiInfoResult {
  ssid: string;
  bssid: string;
  ipAddress: string;
  linkSpeed: number;
}
const WifiInfoPlugin = registerPlugin<{ getInfo(): Promise<WifiInfoResult> }>('WifiInfo');

// ==============================
// Custom Mock Location plugin (MockLocationPlugin.java)
// ==============================
interface MockLocationResult {
  isMock: boolean;
  reason: string;
}
const MockLocationPlugin = registerPlugin<{ check(): Promise<MockLocationResult> }>('MockLocation');

// ==============================
// Custom Microphone Permission plugin (MicrophonePermissionPlugin.java / .swift)
// Minta izin RECORD_AUDIO / AVAudioSession lewat jalur native — sama seperti Camera —
// dipanggil sebelum getUserMedia() supaya izinnya sudah granted di level native lebih dulu.
// ==============================
interface MicrophonePermissionResult {
  granted: boolean;
}
const MicrophonePermissionPlugin = registerPlugin<{
  checkPermission(): Promise<MicrophonePermissionResult>;
  requestPermission(): Promise<MicrophonePermissionResult>;
}>('MicrophonePermission');

function dispatchIncomingCallEvent(data: Record<string, string>) {
  window.dispatchEvent(
    new CustomEvent('socket:call:incoming', {
      detail: {
        room_name: data.room_name,
        caller_id: parseInt(data.caller_id, 10),
        caller_name: data.caller_name,
        livekit_url: data.livekit_url,
      },
    })
  );
}

// Simpan FCM token sementara sebelum user login
let pendingFcmToken: string | null = null;

export function getCapacitorFcmToken(): string | null {
  return pendingFcmToken;
}

// ==============================
// Init semua fitur Capacitor (panggil sekali di App mount)
// ==============================
export async function initCapacitor() {
  if (!isNative) return;

  await SplashScreen.hide({ fadeOutDuration: 300 });

  // Status bar: tidak overlay WebView, background putih, teks/icon gelap
  await StatusBar.setOverlaysWebView({ overlay: false });
  await StatusBar.setStyle({ style: Style.Light });
  await StatusBar.setBackgroundColor({ color: '#ffffff' });

  // Back button: ditangani natively di MainActivity.java (OnBackPressedCallback)
  // double-back to minimize logic ada di sana agar tidak bergantung pada canGoBack SPA
  App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) window.history.back();
  });

  await setupPushNotifications();
}

async function setupPushNotifications() {
  try {
    const result = await PushNotifications.requestPermissions();
    if (result.receive !== 'granted') return;

    await PushNotifications.register();

    PushNotifications.addListener('registration', (token) => {
      pendingFcmToken = token.value;
      // Kirim ke bridge — jika user sudah login, langsung dikirim ke backend
      (window as any).setFcmToken?.(token.value);
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('Push registration error:', err.error);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      // App in foreground — socket.io already delivers call events, but handle call
      // notifications here too as a fallback in case the socket missed the event.
      const data = notification.data as Record<string, string> | undefined;
      if (data?.type === 'incoming_call') {
        dispatchIncomingCallEvent(data);
      }
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      // User tapped a push notification while app was backgrounded or killed.
      const data = action.notification.data as Record<string, string> | undefined;
      if (data?.type === 'incoming_call') {
        // Socket.io was paused so the call:incoming event was missed — re-emit it
        // from the FCM payload so IncomingCallOverlay can show.
        // Small delay: let React finish mounting IncomingCallOverlay before dispatch
        setTimeout(() => dispatchIncomingCallEvent(data), 300);
        return;
      }
      // Semua notifikasi lain (task, chat, dll.) — backend sudah menyertakan `url`
      // tujuan di payload data (lihat buildTargetUrl di notification.service.js).
      // router.navigate() dipakai (bukan useNavigate) karena kode ini berjalan di luar
      // tree React, tidak ada Router context untuk dipakai hook biasa.
      if (data?.url) {
        setTimeout(() => router.navigate(data.url), 300);
      }
    });
  } catch (e) {
    console.error('Push notification setup failed:', e);
  }
}

// ==============================
// Device info
// ==============================
export async function getDeviceInfo() {
  if (!isNative) return null;
  try {
    const [info, idResult] = await Promise.all([Device.getInfo(), Device.getId()]);
    return {
      deviceId: idResult.identifier,
      model: info.model,
      platform: info.platform,
      osVersion: info.osVersion,
      manufacturer: info.manufacturer,
      isVirtual: info.isVirtual,
    };
  } catch {
    return null;
  }
}

// ==============================
// WiFi info (SSID & BSSID)
// Butuh ACCESS_FINE_LOCATION permission + location aktif di Android 8.1+
// ==============================
export async function getWifiInfo() {
  if (!isNative) return null;
  try {
    return await WifiInfoPlugin.getInfo();
  } catch {
    return null;
  }
}

// ==============================
// Mock Location detection (MockLocationPlugin.java)
// Hanya tersedia di native Android. Browser selalu return false.
// ==============================
export async function checkMockLocation(): Promise<boolean> {
  if (!isNative) return false;
  try {
    const result = await MockLocationPlugin.check();
    return result.isMock === true;
  } catch {
    return false;
  }
}

// ==============================
// Microphone permission (native request, lewat MicrophonePermissionPlugin)
// Browser: tidak ada plugin native, getUserMedia() sendiri yang munculkan prompt browser.
// ==============================
export async function ensureMicrophonePermission(): Promise<boolean> {
  if (!isNative) return true;
  try {
    const { granted } = await MicrophonePermissionPlugin.requestPermission();
    return granted;
  } catch {
    return false;
  }
}

// ==============================
// Network status
// ==============================
export async function getNetworkStatus() {
  try {
    return await Network.getStatus();
  } catch {
    return null;
  }
}

// ==============================
// Haptics
// ==============================
export async function vibrate(style: 'light' | 'medium' | 'heavy' = 'medium') {
  if (!isNative) return;
  const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
  await Haptics.impact({ style: map[style] });
}
